import { List } from "immutable";
import { createSelector } from "reselect";

import { ContentType } from "@seneca/course-service-types";

import { ContentId, Contents } from "../models/state/contents/Contents";
import {
  ContentModule,
  ModuleId,
  Modules
} from "../models/state/modules/Modules";
import {
  getAllContentModules,
  getAllContents,
  getSectionContentIds,
  getSectionExerciseContentIds
} from "./state";
import * as contentsSelectors from "./state/contents";
import * as contentSelectors from "./state/contents/content";
import * as modulesSelectors from "./state/modules";

type State = any;

export function makeGetSectionContentModules(
  sectionId: string
): (state: State) => List<ContentModule> {
  return createSelector(
    getAllContents,
    getAllContentModules,
    (state: any) => getSectionContentIds(state, sectionId),
    getContentModulesFromContents
  );
}

export function makeGetSectionContentModuleIds(
  sectionId: string
): (state: State) => List<ModuleId> {
  return createSelector(
    getAllContents,
    (state: any) => getSectionContentIds(state, sectionId),
    getContentModuleIdsFromContents
  );
}

export function makeGetSectionModuleIdsByContentType(
  sectionId: string,
  contentType: ContentType
): (state: State) => List<ModuleId> {
  return createSelector(
    getAllContents,
    (state: any) => getSectionContentIds(state, sectionId),
    (contents: Contents, contentIds: List<string>) => {
      const contentsByType = contents.filter(content => {
        return (content.contentType ?? ContentType.standard) === contentType;
      });

      return getContentModuleIdsFromContents(contentsByType, contentIds);
    }
  );
}

export function makeGetSectionModulesByContentType(
  sectionId: string,
  contentType: ContentType
): (state: State) => List<ContentModule> {
  return createSelector(
    getAllContents,
    getAllContentModules,
    (state: any) => getSectionContentIds(state, sectionId),
    (
      contents: Contents,
      contentModules: Modules,
      sectionContentIds: List<string>
    ) => {
      const sectionContentIdsByType = sectionContentIds.filter(contentId => {
        return (
          (contents.get(contentId)?.contentType ?? ContentType.standard) ===
          contentType
        );
      });

      return getContentModulesFromContents(
        contents,
        contentModules,
        sectionContentIdsByType
      );
    }
  );
}
export function makeGetSectionExerciseContentModules(
  sectionId: string
): (state: State) => List<ContentModule> {
  return createSelector(
    getAllContents,
    getAllContentModules,
    (state: any) => getSectionExerciseContentIds(state, sectionId),
    getContentModulesFromContents
  );
}

export function makeGetSectionExerciseContentModuleIds(
  sectionId: string
): (state: State) => List<ModuleId> {
  return createSelector(
    getAllContents,
    (state: any) => getSectionExerciseContentIds(state, sectionId),
    getContentModuleIdsFromContents
  );
}

export function getContentModulesFromContents(
  contents: Contents,
  contentModules: Modules,
  contentIds: List<ContentId>
): List<ContentModule> {
  return getContentModuleIdsFromContents(contents, contentIds).map(moduleId =>
    modulesSelectors.getContentModule(contentModules, moduleId)
  ) as List<ContentModule>;
}

export function getContentModuleIdsFromContents(
  contents: Contents,
  contentIds: List<ContentId>
): List<ModuleId> {
  return contentsSelectors
    .listExistingContents(contents, contentIds)
    .map(contentSelectors.getContentModuleIds)
    .flatten() as List<ModuleId>;
}
